/**
 * ============================================================
 * ELOISE ACCESSOIRES — interactions.js
 * Comportements frontend : révélations, hover premium
 *
 * Responsabilités :
 *   - IntersectionObserver → révélations au scroll
 *   - Hover states JS (complément au CSS pur)
 *   - Scroll horizontal snap (collections)
 *   - Compteur de quantité produit
 *   - Gestion des sections vidéo (autoplay au viewport)
 *
 * Ce module est toujours chargé — il gère les comportements
 * qui ne peuvent pas être résolus en CSS seul.
 *
 * prefers-reduced-motion : respecté sur toutes les animations
 * ============================================================
 */

'use strict';


// ============================================================
// CONFIGURATION
// ============================================================

const REVEAL_CONFIG = {
  rootMargin:  '0px 0px -60px 0px', // Déclenche 60px avant la visibilité complète
  threshold:   0.1,                  // 10% de l'élément visible suffit
};

const VIDEO_AUTOPLAY_CONFIG = {
  rootMargin: '0px',
  threshold:  0.5, // 50% visible pour déclencher l'autoplay
};


// ============================================================
// INIT
// ============================================================

document.addEventListener('DOMContentLoaded', () => {

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  initScrollReveals(prefersReduced);
  initSectionReveals(prefersReduced);
  initVideoAutoplay();
  initHorizontalScroll();
  initQuantityInputs();
  initDirectAddToCart();

});


// ============================================================
// RÉVÉLATIONS AU SCROLL
// Les éléments [data-reveal] sont initialement invisibles (CSS).
// L'IntersectionObserver ajoute .is-revealed quand ils sont visibles.
// ============================================================

function initScrollReveals(prefersReduced) {

  const revealElements = document.querySelectorAll('[data-reveal]');
  if (!revealElements.length) return;

  // Si prefers-reduced-motion, tout apparaît immédiatement
  if (prefersReduced) {
    revealElements.forEach(el => el.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {

      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        // Un seul déclenchement — on arrête d'observer
        observer.unobserve(entry.target);
      }

    });
  }, REVEAL_CONFIG);

  revealElements.forEach(el => observer.observe(el));
}


// ============================================================
// SECTION REVEALS — Ajoute data-revealed sur les sections
// Déclenche les animations CSS des enfants séquencés dans
// les nouvelles sections (lifestyle, matière, fabrication, atelier)
// ============================================================

function initSectionReveals(prefersReduced) {

  const sections = document.querySelectorAll('[data-reveal-section]');
  if (!sections.length) return;

  if (prefersReduced) {
    sections.forEach(section => section.setAttribute('data-revealed', ''));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.setAttribute('data-revealed', '');
        observer.unobserve(entry.target);
      }
    });
  }, {
    rootMargin: '0px 0px -80px 0px',
    threshold:  0.08,
  });

  sections.forEach(section => observer.observe(section));
}


// ============================================================
// ADD-TO-CART DIRECT — Boutons sans formulaire
// Utilisé par best-sellers et autres sections qui ne wrappent
// pas dans un <form action="/cart/add">
// ============================================================

function initDirectAddToCart() {

  document.addEventListener('click', async (e) => {

    const btn = e.target.closest('[data-add-to-cart][data-product-id]');
    if (!btn) return;

    // Bouton dans un formulaire : laisser cart.js gérer
    if (btn.closest('form[action="/cart/add"]')) return;

    const variantId = btn.dataset.productId;
    if (!variantId) return;

    const originalText = btn.textContent.trim();
    btn.disabled = true;
    btn.textContent = 'Ajout…';

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    try {
      await window.EloiseCart?.add(formData);
      if (window.EloiseTheme?.toast) {
        window.EloiseTheme.toast('Article ajouté au panier', 'success');
      }
      window.EloiseCart?.open();
    } catch (err) {
      if (window.EloiseTheme?.toast) {
        window.EloiseTheme.toast(err.message || 'Impossible d\'ajouter cet article', 'error');
      }
    } finally {
      btn.disabled   = false;
      btn.textContent = originalText;
    }
  });
}


// ============================================================
// AUTOPLAY VIDÉO AU VIEWPORT
// Les vidéos (hors hero) jouent uniquement quand visibles.
// Pause automatique quand elles sortent du viewport.
// ============================================================

function initVideoAutoplay() {

  // Sélectionner uniquement les vidéos qui ne sont pas le hero
  // (le hero gère son propre autoplay via l'attribut HTML)
  const videos = document.querySelectorAll(
    'video:not(.hero-immersive__video)'
  );

  if (!videos.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const video = entry.target;

      if (entry.isIntersecting) {
        // Jouer uniquement si la vidéo est muted (autoplay policy)
        if (video.muted) {
          video.play().catch(() => {
            // Silencieux — l'autoplay peut être bloqué par le navigateur
          });
        }
      } else {
        video.pause();
      }
    });
  }, VIDEO_AUTOPLAY_CONFIG);

  videos.forEach(video => observer.observe(video));
}


// ============================================================
// SCROLL HORIZONTAL — Collections
// Amélioration JS du scroll horizontal snap
// Ajoute des contrôles de navigation (prev/next) si nécessaire
// ============================================================

function initHorizontalScroll() {

  const tracks = document.querySelectorAll('[data-horizontal-scroll]');
  if (!tracks.length) return;

  tracks.forEach(track => {
    // Vérifier si le contenu dépasse (donc scroll nécessaire)
    const hasOverflow = track.scrollWidth > track.clientWidth;
    if (!hasOverflow) return;

    // Signal visuel : indique qu'il y a plus de contenu (peak de la prochaine carte)
    track.setAttribute('data-has-scroll', 'true');

    // Gestion du scroll par touch (momentum amélioré)
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    track.addEventListener('mousedown', (e) => {
      isDown = true;
      track.style.cursor = 'grabbing';
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    });

    track.addEventListener('mouseleave', () => {
      isDown = false;
      track.style.cursor = '';
    });

    track.addEventListener('mouseup', () => {
      isDown = false;
      track.style.cursor = '';
    });

    track.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 1.5;
      track.scrollLeft = scrollLeft - walk;
    });

  });
}


// ============================================================
// COMPTEUR DE QUANTITÉ PRODUIT
// Boutons +/- autour d'un input[type=number]
// ============================================================

function initQuantityInputs() {

  // Délégation — les inputs peuvent être dans un drawer ajouté dynamiquement
  document.addEventListener('click', (e) => {

    const btn = e.target.closest('[data-quantity-btn]');
    if (!btn) return;

    const wrapper = btn.closest('[data-quantity-wrapper]');
    if (!wrapper) return;

    const input = wrapper.querySelector('input[type="number"]');
    if (!input) return;

    const action = btn.dataset.quantityBtn; // 'increase' | 'decrease'
    const currentVal = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 999;

    if (action === 'increase' && currentVal < max) {
      input.value = currentVal + 1;
    } else if (action === 'decrease' && currentVal > min) {
      input.value = currentVal - 1;
    }

    // Déclencher un event change pour les listeners downstream
    input.dispatchEvent(new Event('change', { bubbles: true }));

    // Accessibilité : mise à jour du label live region
    const liveRegion = wrapper.querySelector('[role="status"]');
    if (liveRegion) {
      liveRegion.textContent = `Quantité : ${input.value}`;
    }

  });
}


// ============================================================
// HOVER HALO — Effet de lumière ambrée au hover
// Uniquement sur les devices avec hover (pas mobile)
// et uniquement sur les sections sombres
// ============================================================

function initHoverHalo() {

  // Ne s'active que si le device supporte hover
  if (!window.matchMedia('(hover: hover)').matches) return;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return;

  // Sélectionner les cartes sur fond sombre qui ont le modificateur glow
  const glowCards = document.querySelectorAll('.card-hover-glow');

  glowCards.forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      // Positionner le halo sur le curseur
      card.style.setProperty('--halo-x', `${x}%`);
      card.style.setProperty('--halo-y', `${y}%`);
    });
  });
}

// Halo activé après premier interaction pour éviter le calcul inutile
document.addEventListener('mousemove', initHoverHalo, { once: true });


// ============================================================
// LAZY IMAGES — Fallback pour navigateurs sans loading="lazy"
// Shopify CDN génère des URLs srcset — le fallback est simple
// ============================================================

function initLazyImageFallback() {

  // Vérification du support natif
  if ('loading' in HTMLImageElement.prototype) return;

  // Fallback : IntersectionObserver pour charger les images
  const lazyImages = document.querySelectorAll('img[loading="lazy"]');
  if (!lazyImages.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;

        // Transférer data-src → src si présent
        if (img.dataset.src) {
          img.src = img.dataset.src;
        }

        observer.unobserve(img);
      }
    });
  }, { rootMargin: '200px 0px' });

  lazyImages.forEach(img => observer.observe(img));
}

// Exécution immédiate (pas besoin d'attendre DOMContentLoaded)
initLazyImageFallback();
